import { Transfer, TransferCreationRequest, Scheduling, Audience } from 'src/app/models/services/audience';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  AbstractControl,
  ValidatorFn
} from '@angular/forms';
import { TransfersAttributes } from 'src/app/models/services/transfersAttributes.enum';
import { TargetPlatforms } from 'src/app/models/services/targetPlatforms.enum';
import { TargetPlatformsTypes } from 'src/app/models/services/targetPlatformsType.enum';
import { IdentifiersTypes } from 'src/app/models/services/identifiersTypes.enum';
import { UsersService } from 'src/app/services/users.service';
import { AtLeastOneChecked } from 'src/app/services/core/validators/atLeastOne-validator';
import { TargetPlatform } from 'src/app/models/services/audiences/target-platform';
import { TransfersService } from 'src/app/services/audiences/transfers.service';
import { Subscription } from 'rxjs';
import { TransferFileAction } from 'src/app/models/services/transferFileAction.enum';

export class IdGrapTypeSelection {
  identifierTypeKey: number;
  count: number;
}

@Component({
  selector: 'eris-transfer-parameters-dialog',
  templateUrl: './transfer-parameters-dialog.component.html',
  styleUrls: ['./transfer-parameters-dialog.component.sass']
})
export class TransferParametersDialogComponent implements OnInit, OnDestroy {
  public audience: Audience;
  public transfer: Transfer;
  public accept: (data: Transfer) => void;
  public dismiss: () => void;
  public loading: boolean = true;

  form: FormGroup;
  name: string;
  title: string;

  targetPlatforms: any = TargetPlatforms;

  public platformGroupOrder: Array<TargetPlatformsTypes | string> = [
    TargetPlatformsTypes.DSP,
    TargetPlatformsTypes.TV,
    TargetPlatformsTypes.Other,
  ];

  public platformItemOrderByGroup: { [key: string]: Array<number | string> } = {
    [TargetPlatformsTypes.DSP]: [
      TargetPlatforms.Meta,
      TargetPlatforms.TikTok,
      TargetPlatforms.TheTradeDesk,
      TargetPlatforms.Amazon,
      TargetPlatforms.DV360,
    ],
    [TargetPlatformsTypes.TV]: [
      TargetPlatforms.Movistar,
      TargetPlatforms.A3Media,
      TargetPlatforms.Mediaset,
      TargetPlatforms.Netflix
    ],
    [TargetPlatformsTypes.Other]: [
      TargetPlatforms.TapTap,
      TargetPlatforms.WAMProxy,
      TargetPlatforms.Xandr
    ]
  };

  public readonly TransferFileAction = TransferFileAction;

  public platformItemDefaultOrder: 'name' | 'id' = 'id';
  availablePlatforms: TargetPlatform[] = [];

  availableIdentifiersTypesSelections: IdGrapTypeSelection[];
  identifiersTypes: any = IdentifiersTypes;

  tapTapAudienceName: string;
  tapTapAudienceCategoryName: string;
  a3MediaAudienceName: string;
  a3MediaAudienceCategoryName: string;

  audiencesSchedulings: Scheduling[] = [
    { id: 1, description: 'No scheduling' },
    { id: 2, description: 'Weekly' },
    { id: 3, description: 'Bi-weekly' },
    { id: 4, description: 'Monthly' },
    { id: 5, description: 'Each two days' }
  ];

  // ---- Download mode helpers ----
  private tiktokModeSub?: Subscription;
  private cachedValidators = new Map<AbstractControl, ValidatorFn | ValidatorFn[] | null>();
  private isTikTokDownloadMode = false;

  private prevCreateFileValue: any = null;
  private prevSendAfterValue: any = null;
  private prevSendAfterDisabled: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private usersService: UsersService,
    private transfersService: TransfersService
  ) {}

  ngOnInit(): void {
    this.title = !this.transfer ? 'New transfer' : 'Edit transfer';

    this.form = !this.transfer
      ? this.formBuilder.group({
          transferName: ['', Validators.required],
          schedulingId: [1, Validators.required],
          schedulingEndDate: [''],

          // IMPORTANTE: boolean
          createFile: [false],
          sendAfterCreate: [false],

          targetPlatform: [undefined, Validators.required],

          customSegmentAttributes: this.formBuilder.group({}),
          metaAttributes: this.formBuilder.group({}),
          amazonAttributes: this.formBuilder.group({}),
          tapTapAttributes: this.formBuilder.group({}),
          xandrAttributes: this.formBuilder.group({}),
          movistarAttributes: this.formBuilder.group({}),
          a3MediaAttributes: this.formBuilder.group({}),
          netflixAttributes: this.formBuilder.group({}),
          ttdAttributes: this.formBuilder.group({}),
          dv360Attributes: this.formBuilder.group({}),
          tiktokAttributes: this.formBuilder.group({}),

          targetIdentifiers: this.formBuilder.group({})
        })
      : this.formBuilder.group({
          transferName: [this.transfer.name, Validators.required],
          schedulingId: [this.transfer.schedulingId, Validators.required],
          schedulingEndDate: [this.transfer.schedulingEndDate]
        });

    if (!this.transfer) {
      this.availableIdentifiersTypesSelections = [];
      const isAdmin: boolean = this.usersService.hasGlobalSectionRight('Admin');

      this.transfersService.getTargetPlatforms().subscribe({
        next: (data: TargetPlatform[]) => {
          const platforms = Array.isArray(data) ? data : [];
          this.availablePlatforms = this.applyPlatformAvailability(platforms, isAdmin);
          this.loading = false;
        },
        error: err => {
          console.error(err);
          this.loading = false;
        }
      });
    } else {
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.tiktokModeSub?.unsubscribe();
  }

  getIdentifierCount = (idGraphType: IdentifiersTypes): number => {
    return this.audience.identifiersCounts[idGraphType] ?? 0;
  }

  getHomeIdsCount = (): number => {
    return this.audience.homesCount ?? 0;
  }

  onAccept = (): void => {
    let date: Date = this.form.controls.schedulingEndDate?.value;
    let schedulingEndDateUTC: Date;

    if (!!date) {
      if (!!(date as Date)) date = new Date(date);

      const year = date.getFullYear();
      const month = date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1);
      const day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();

      schedulingEndDateUTC = new Date(`${year}-${month}-${day}T00:00:00Z`);
    }

    if (!this.transfer) {
      const result: TransferCreationRequest = {
        name: this.form.controls.transferName.value,
        schedulingId: +this.form.controls.schedulingId.value,
        schedulingEndDate: !!schedulingEndDateUTC ? schedulingEndDateUTC : null,
        targetPlatform: +this.form.controls.targetPlatform.value,
        customAttributes: [],
        identifiersTypes: [],
        files: [],
        audience: this.audience,

        createFile: this.form.controls.createFile.value,
        sendAfterCreate: this.form.controls.sendAfterCreate.value
      };

      switch (result.targetPlatform) {
        case TargetPlatforms.WAMProxy:
          result.customAttributes.push({
            attributeId: TransfersAttributes.CustomSegmentId,
            value: this.form.get('customSegmentAttributes.customSegmentId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.CustomSegmentDescription,
            value: this.form.get('customSegmentAttributes.customSegmentDescription').value
          });
          break;

        case TargetPlatforms.Meta:
          result.customAttributes.push({
            attributeId: TransfersAttributes.ProfileExternalIdentifier,
            value: this.form.get('metaAttributes.businessId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AdvertiserExternalIdentifier,
            value: this.form.get('metaAttributes.adAccountId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalIdentifier,
            value: this.form.get('metaAttributes.audienceId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalName,
            value: this.form.get('metaAttributes.audienceName').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceSharedAccounts,
            value: JSON.stringify(this.form.get('metaAttributes.sharedAccounts').value)
          });
          break;

        case TargetPlatforms.Amazon:
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalIdentifier,
            value: this.form.get('amazonAttributes.audienceId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalName,
            value: this.form.get('amazonAttributes.audienceName').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.ProfileExternalIdentifier,
            value: this.form.get('amazonAttributes.profileId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AdvertiserExternalIdentifier,
            value: this.form.get('amazonAttributes.advertiserId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalCategoryName,
            value: this.form.get('amazonAttributes.datasetName').value
          });
          break;

        case TargetPlatforms.Xandr:
          result.customAttributes.push({
            attributeId: TransfersAttributes.ProfileExternalIdentifier,
            value: this.form.get('xandrAttributes.accountId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalIdentifier,
            value: this.form.get('xandrAttributes.segmentId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalName,
            value: this.form.get('xandrAttributes.segmentName').value
          });
          break;

        case TargetPlatforms.TapTap:
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalName,
            value: this.form.get('tapTapAttributes.audienceName').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalCategoryName,
            value: this.form.get('tapTapAttributes.categoryName').value
          });
          break;

        case TargetPlatforms.Movistar:
          result.customAttributes.push(
            {
              attributeId: TransfersAttributes.AudienceExternalIdentifier,
              value: this.form.get('movistarAttributes.audienceId').value
            },
            {
              attributeId: TransfersAttributes.AudienceExternalName,
              value: this.form.get('movistarAttributes.audienceName').value
            }
          );
          break;

        case TargetPlatforms.A3Media:
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalName,
            value: this.form.get('a3MediaAttributes.audienceName').value
          });
          break;

        case TargetPlatforms.TheTradeDesk:
          result.customAttributes.push({
            attributeId: TransfersAttributes.FirstPartyData,
            value: this.form.get('ttdAttributes.firstPartyData').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.ProfileExternalIdentifier,
            value: this.form.get('ttdAttributes.partnerId').value
          });
          if (this.form.get('ttdAttributes.firstPartyData').value == '0') {
            result.customAttributes.push({
              attributeId: TransfersAttributes.PercentOfMediaCostRate,
              value: this.form.get('ttdAttributes.percentOfMediaCostRate').value
            });
            result.customAttributes.push({
              attributeId: TransfersAttributes.CPMRateAmount,
              value: this.form.get('ttdAttributes.cpmRateAmount').value
            });
          } else {
            result.customAttributes.push({
              attributeId: TransfersAttributes.AdvertiserExternalIdentifier,
              value: this.form.get('ttdAttributes.advertiserId').value
            });
          }
          break;

        case TargetPlatforms.TikTok:
          result.customAttributes.push({
            attributeId: TransfersAttributes.ProfileExternalIdentifier,
            value: this.form.get('tiktokAttributes.businessId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AdvertiserExternalIdentifier,
            value: this.form.get('tiktokAttributes.advertiserId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalIdentifier,
            value: this.form.get('tiktokAttributes.audienceId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalName,
            value: this.form.get('tiktokAttributes.audienceName').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalSubType,
            value: this.form.get('tiktokAttributes.audienceSubType').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceSharedAccounts,
            value: JSON.stringify(this.form.get('tiktokAttributes.sharedAdvertisers').value)
          });
          break;

        case TargetPlatforms.DV360:
          result.customAttributes.push({
            attributeId: TransfersAttributes.ProfileExternalIdentifier,
            value: this.form.get('dv360Attributes.partnerId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AdvertiserExternalIdentifier,
            value: this.form.get('dv360Attributes.advertiserId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalIdentifier,
            value: this.form.get('dv360Attributes.audienceId').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalName,
            value: this.form.get('dv360Attributes.audienceName').value
          });
          result.customAttributes.push({
            attributeId: TransfersAttributes.AudienceExternalSubType,
            value: this.form.get('dv360Attributes.audienceSubType').value
          });
          break;

        default:
          break;
      }

      // IdentifiersTypes
      Object.keys(this.form.get('targetIdentifiers')['controls'])
        .filter(x => this.form.get('targetIdentifiers')['controls'][x].value)
        .forEach(x => result.identifiersTypes.push(this.identifiersTypes[x]));

      this.accept(result);

    } else {
      this.transfer.name = this.form.controls.transferName.value;
      this.transfer.schedulingId = +this.form.controls.schedulingId.value;
      this.transfer.schedulingEndDate = !!schedulingEndDateUTC ? schedulingEndDateUTC : null;

      this.accept(this.transfer);
    }
  };

  onDismiss = (): void => {
    this.dismiss();
  }

  onTransferNameChange = (): void => {
    this.updateValueAndValidity();
  }

  onPlatformSelected(p: TargetPlatform) {
    this.form.get('targetPlatform')!.setValue(p.id);

    this.configureAttributeGroups(p);

    this.availableIdentifiersTypesSelections = this.getIdGraphSelectionsFor(p.id as TargetPlatforms);
    this.rebuildTargetIdentifiersControls(this.availableIdentifiersTypesSelections);

    // Re-aplicar modo download si corresponde (por si el rebuild reintroduce validators)
    if (p.id === TargetPlatforms.TikTok) {
      const mode = String(this.form.get('tiktokAttributes.newAudienceData')?.value);
      this.applyTikTokDownloadMode(mode === '2');
    } else {
      this.tiktokModeSub?.unsubscribe();
      this.applyTikTokDownloadMode(false);
    }

    this.form.updateValueAndValidity();
  }

  public updateValueAndValidity = (): void => {
    this.form.updateValueAndValidity();
  }

  // ---------------------------
  // Download file mode logic
  // ---------------------------
  private cacheValidatorsRec(ctrl: AbstractControl): void {
    if (!this.cachedValidators.has(ctrl)) {
      this.cachedValidators.set(ctrl, ctrl.validator);
    }
    const anyCtrl: any = ctrl as any;
    if (anyCtrl?.controls) {
      Object.values(anyCtrl.controls).forEach((c: AbstractControl) => this.cacheValidatorsRec(c));
    }
  }

  private clearValidatorsExceptTransferName(): void {
    const keep = this.form.get('transferName');

    const walk = (ctrl: AbstractControl) => {
      if (ctrl !== keep) {
        ctrl.clearValidators();
        ctrl.clearAsyncValidators();
      }
      const anyCtrl: any = ctrl as any;
      if (anyCtrl?.controls) {
        Object.values(anyCtrl.controls).forEach((c: AbstractControl) => walk(c));
      }
      ctrl.updateValueAndValidity({ emitEvent: false });
    };

    walk(this.form);
  }

  private restoreCachedValidators(): void {
    for (const [ctrl, v] of this.cachedValidators.entries()) {
      ctrl.setValidators(v as any);
      ctrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private applyTikTokDownloadMode(isDownload: boolean): void {
    this.isTikTokDownloadMode = isDownload;

    const createFileCtrl = this.form.get('createFile');
    const sendAfterCtrl = this.form.get('sendAfterCreate');

    if (isDownload) {
      // Guardar estado previo (para restaurar al salir de download)
      this.prevCreateFileValue = createFileCtrl?.value;
      this.prevSendAfterValue = sendAfterCtrl?.value;
      this.prevSendAfterDisabled = !!sendAfterCtrl?.disabled;

      // Forzar: createFile true, sendAfterCreate false y bloqueado
      createFileCtrl?.setValue(true, { emitEvent: false });
      sendAfterCtrl?.setValue(false, { emitEvent: false });
      sendAfterCtrl?.disable({ emitEvent: false });

      // Solo se valida transferName
      this.cachedValidators.clear();
      this.cacheValidatorsRec(this.form);
      this.clearValidatorsExceptTransferName();

    } else {
      // Restaurar valores previos (sin obligar nada)
      if (createFileCtrl && this.prevCreateFileValue !== null) {
        createFileCtrl.setValue(this.prevCreateFileValue, { emitEvent: false });
      }

      if (sendAfterCtrl) {
        if (!this.prevSendAfterDisabled) {
          sendAfterCtrl.enable({ emitEvent: false });
        }
        if (this.prevSendAfterValue !== null) {
          sendAfterCtrl.setValue(this.prevSendAfterValue, { emitEvent: false });
        }
      }

      // Restaurar validadores originales
      this.restoreCachedValidators();
    }

    this.form.updateValueAndValidity({ emitEvent: false });
  }

  // ---------------------------
  // Platforms availability
  // ---------------------------
  private computeAllowedPlatformIds(platforms: TargetPlatform[], isAdmin: boolean): number[] {
    const get = (t: IdentifiersTypes) => this.getIdentifierCount(t) || 0;
    const enabledInSrv = (id?: number) => id != null && platforms.some(p => p.id === id && p.enabled);

    const ids: number[] = [];
    const add = (id: number | undefined) => {
      if (id != null && enabledInSrv(id)) ids.push(id);
    };

    if (get(IdentifiersTypes.WAMCookie) + get(IdentifiersTypes.MAID) > 0) add(TargetPlatforms.WAMProxy);

    if (get(IdentifiersTypes.HashedEmail) + get(IdentifiersTypes.MAID) > 0) {
      add(TargetPlatforms.Meta);
      add(TargetPlatforms.TikTok);
      if (isAdmin) {
        add(TargetPlatforms.TapTap);
        add(TargetPlatforms.Amazon);
      }
    }

    if (isAdmin && get(IdentifiersTypes.HashedEmail) > 0) {
      add(TargetPlatforms.Movistar);
      add(TargetPlatforms.A3Media);
      add(TargetPlatforms.Netflix);
    }

    if (isAdmin && (get(IdentifiersTypes.XandrCookie) + get(IdentifiersTypes.MAID) > 0)) add(TargetPlatforms.Xandr);
    if (isAdmin && (get(IdentifiersTypes.TTDCookie) + get(IdentifiersTypes.MAID) > 0)) add(TargetPlatforms.TheTradeDesk);
    if (isAdmin) add(TargetPlatforms.TapTapOOH);
    if (this.getHomeIdsCount() > 0) add(TargetPlatforms.Mediaset);

    return Array.from(new Set(ids));
  }

  private applyPlatformAvailability(platforms: TargetPlatform[], isAdmin: boolean): TargetPlatform[] {
    const allowed = new Set(this.computeAllowedPlatformIds(platforms, isAdmin));
    return platforms.map(p => ({
      ...p,
      enabled: p.id != null ? (p.enabled && allowed.has(p.id)) : false,
    }));
  }

  private setGroup(name: string, controls: { [key: string]: any }) {
    this.form.setControl(name, this.formBuilder.group(controls));
  }

  private resetAllAttributeGroups() {
    this.setGroup('customSegmentAttributes', {});
    this.setGroup('metaAttributes', {});
    this.setGroup('amazonAttributes', {});
    this.setGroup('tapTapAttributes', {});
    this.setGroup('xandrAttributes', {});
    this.setGroup('ttdAttributes', {});
    this.setGroup('movistarAttributes', {});
    this.setGroup('a3MediaAttributes', {});
    this.setGroup('netflixAttributes', {});
    this.setGroup('tiktokAttributes', {});
    this.setGroup('dv360Attributes', {});
  }

  private configureAttributeGroups(p: TargetPlatform) {
    // Si veníamos de TikTok, limpiamos subscripción y restauramos modo normal
    if (p.id !== TargetPlatforms.TikTok) {
      this.tiktokModeSub?.unsubscribe();
      this.applyTikTokDownloadMode(false);
    }

    this.resetAllAttributeGroups();

    switch (p.id) {
      case TargetPlatforms.WAMProxy:
        this.setGroup('customSegmentAttributes', {
          customSegmentId: [''],
          customSegmentDescription: ['', Validators.required],
        });
        break;

      case TargetPlatforms.Meta:
        this.setGroup('metaAttributes', {
          businessId: ['', Validators.required],
          adAccountId: ['', Validators.required],
          audienceId: [''],
          audienceName: ['', Validators.required],
          sharedAccounts: [[]],
        });
        break;

      case TargetPlatforms.Amazon:
        this.setGroup('amazonAttributes', {
          profileId: ['', Validators.required],
          advertiserId: ['', Validators.required],
          audienceId: [''],
          audienceName: ['', Validators.required],
          datasetName: [
            '',
            [
              Validators.required,
              Validators.minLength(10),
              Validators.maxLength(100),
              Validators.pattern(/^[A-Za-z][A-Za-z0-9_-]*$/)
            ]
          ],
        });
        break;

      case TargetPlatforms.TapTap:
        this.configureTapTapDefaults(p.id);
        break;

      case TargetPlatforms.Xandr:
        this.setGroup('xandrAttributes', {
          accountId: ['', Validators.required],
          segmentId: [''],
          segmentName: ['', Validators.required],
        });
        break;

      case TargetPlatforms.Movistar:
        this.setGroup('movistarAttributes', {
          audienceId: [null],
          audienceName: [this.audience.name, Validators.required],
        });
        break;

      case TargetPlatforms.A3Media:
        this.configureA3MediaDefaults(p.id);
        break;

      case TargetPlatforms.TheTradeDesk:
        this.setGroup('ttdAttributes', {
          firstPartyData: [1, Validators.required],
          partnerId: ['', Validators.required],
          advertiserId: [''],
          percentOfMediaCostRate: [''],
          cpmRateAmount: [''],
        });
        break;

      case TargetPlatforms.TikTok:
        this.setGroup('tiktokAttributes', {
          newAudienceData: ['0', Validators.required], // 0/1/2
          businessId: ['', Validators.required],
          advertiserId: ['', Validators.required],
          audienceId: [''],
          audienceName: ['', Validators.required],
          audienceSubType: ['NORMAL', Validators.required],
          sharedAdvertisers: [[]],
        });

        // Escucha cambios del selector (0/1/2)
        this.tiktokModeSub?.unsubscribe();
        const modeCtrl = this.form.get('tiktokAttributes.newAudienceData');
        this.tiktokModeSub = modeCtrl?.valueChanges.subscribe(v => {
          this.applyTikTokDownloadMode(String(v) === '2');
        });

        // Aplica estado inicial
        this.applyTikTokDownloadMode(String(modeCtrl?.value) === '2');
        break;

      case TargetPlatforms.DV360:
        this.setGroup('dv360Attributes', {
          newAudienceData: [1, Validators.required],
          partnerId: ['', Validators.required],
          advertiserId: ['', Validators.required],
          audienceId: [''],
          audienceName: ['', Validators.required],
          audienceSubType: ['NORMAL', Validators.required],
        });
        break;

      default:
        break;
    }
  }

  private configureTapTapDefaults(platformId: number) {
    if (this.audience?.transfers?.length) {
      const last = this.audience.transfers
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
        .find(x => x.targetPlatform === platformId);

      if (last) {
        this.tapTapAudienceCategoryName =
          last.customAttributes.find(x => x.attributeId === TransfersAttributes.AudienceExternalCategoryName)?.value ?? '';
        this.tapTapAudienceName =
          last.customAttributes.find(x => x.attributeId === TransfersAttributes.AudienceExternalName)?.value ?? this.audience.name;
      }
    }
    if (!this.tapTapAudienceCategoryName) this.tapTapAudienceCategoryName = '';
    if (!this.tapTapAudienceName) this.tapTapAudienceName = this.audience.name;

    this.setGroup('tapTapAttributes', {
      audienceName: [this.tapTapAudienceName, Validators.required],
      categoryName: [this.tapTapAudienceCategoryName, Validators.required],
    });
  }

  private configureA3MediaDefaults(platformId: number) {
    if (this.audience?.transfers?.length) {
      const last = this.audience.transfers
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
        .find(x => x.targetPlatform === platformId);

      if (last) {
        this.a3MediaAudienceName =
          last.customAttributes.find(x => x.attributeId === TransfersAttributes.AudienceExternalName)?.value
          ?? this.audience.name;
      }
    }
    if (!this.a3MediaAudienceName) this.a3MediaAudienceName = this.audience.name;

    this.setGroup('a3MediaAttributes', {
      audienceName: [this.a3MediaAudienceName, Validators.required],
    });
  }

  private getIdGraphSelectionsFor(platformId: TargetPlatforms): IdGrapTypeSelection[] {
    const c = (t: IdentifiersTypes) => this.audience.identifiersCounts[t] ?? 0;

    switch (platformId) {
      case TargetPlatforms.WAMProxy:
        return [
          { identifierTypeKey: IdentifiersTypes.WAMCookie, count: c(IdentifiersTypes.WAMCookie) },
          { identifierTypeKey: IdentifiersTypes.MAID, count: c(IdentifiersTypes.MAID) },
          { identifierTypeKey: IdentifiersTypes.CTV, count: c(IdentifiersTypes.CTV) },
        ];

      case TargetPlatforms.Meta:
      case TargetPlatforms.TikTok:
      case TargetPlatforms.Amazon:
      case TargetPlatforms.DV360:
        return [
          { identifierTypeKey: IdentifiersTypes.HashedEmail, count: c(IdentifiersTypes.HashedEmail) },
          { identifierTypeKey: IdentifiersTypes.MAID, count: c(IdentifiersTypes.MAID) },
          { identifierTypeKey: IdentifiersTypes.CTV, count: c(IdentifiersTypes.CTV) },
        ];

      case TargetPlatforms.UnityOnboarding:
      case TargetPlatforms.Movistar:
      case TargetPlatforms.A3Media:
      case TargetPlatforms.Netflix:
        return [
          { identifierTypeKey: IdentifiersTypes.HashedEmail, count: c(IdentifiersTypes.HashedEmail) },
        ];

      case TargetPlatforms.TapTap:
        return [
          { identifierTypeKey: IdentifiersTypes.MAID, count: c(IdentifiersTypes.MAID) },
          { identifierTypeKey: IdentifiersTypes.CTV, count: c(IdentifiersTypes.CTV) },
        ];

      case TargetPlatforms.Xandr:
        return [
          { identifierTypeKey: IdentifiersTypes.XandrCookie, count: c(IdentifiersTypes.XandrCookie) },
          { identifierTypeKey: IdentifiersTypes.MAID, count: c(IdentifiersTypes.MAID) },
          { identifierTypeKey: IdentifiersTypes.CTV, count: c(IdentifiersTypes.CTV) },
        ];

      case TargetPlatforms.TheTradeDesk:
        return [
          { identifierTypeKey: IdentifiersTypes.TTDCookie, count: c(IdentifiersTypes.TTDCookie) },
          { identifierTypeKey: IdentifiersTypes.MAID, count: c(IdentifiersTypes.MAID) },
          { identifierTypeKey: IdentifiersTypes.CTV, count: c(IdentifiersTypes.CTV) },
        ];

      default:
        return [];
    }
  }

  private rebuildTargetIdentifiersControls(selections: IdGrapTypeSelection[]) {
    const controls: Record<string, FormControl> = {};
    selections.forEach(x => {
      const key = this.identifiersTypes[x.identifierTypeKey];
      controls[key] = new FormControl({ value: x.count > 0, disabled: x.count === 0 });
    });

    this.form.setControl(
      'targetIdentifiers',
      new FormGroup(controls, { validators: AtLeastOneChecked })
    );

    if (this.isTikTokDownloadMode) {
      this.applyTikTokDownloadMode(true);
    }
  }
}
